class DetailsDisclosure extends HTMLElement {
  constructor() {
    super();
    this.mainDetailsToggle = this.querySelector('details');
    this.content = this.mainDetailsToggle.querySelector('summary').nextElementSibling;

    this.mainDetailsToggle.addEventListener('focusout', this.onFocusOut.bind(this));
    this.mainDetailsToggle.addEventListener('toggle', this.onToggle.bind(this));
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    });
  }

  onToggle() {
    if (!this.animations) this.animations = this.content.getAnimations();

    if (this.mainDetailsToggle.hasAttribute('open')) {
      this.animations.forEach((animation) => animation.play());
    } else {
      this.animations.forEach((animation) => animation.cancel());
    }
  }

  close() {
    this.mainDetailsToggle.removeAttribute('open');
    this.mainDetailsToggle.querySelector('summary').setAttribute('aria-expanded', false);
  }
}

customElements.define('details-disclosure', DetailsDisclosure);

class HeaderMenu extends DetailsDisclosure {
  constructor() {
    super();
    this.header = document.querySelector('.header-wrapper');
    this.summary = this.mainDetailsToggle.querySelector('summary');
    this.closeTimer = null;
    this.onWindowResize = this.onWindowResize.bind(this);

    this.addEventListener('mouseenter', this.onMouseEnter.bind(this));
    this.addEventListener('mouseleave', this.onMouseLeave.bind(this));
    this.summary.addEventListener('click', this.onSummaryClick.bind(this));
    window.addEventListener('resize', this.onWindowResize);
  }

  disconnectedCallback() {
    window.removeEventListener('resize', this.onWindowResize);
  }

  onWindowResize() {
    if (!this.mainDetailsToggle.open) {
      this.resetSimpleDropdownPosition();
      return;
    }

    this.positionSimpleDropdown();
  }

  shouldUseHoverInteraction() {
    return (
      window.matchMedia('(min-width: 990px)').matches &&
      window.matchMedia('(hover: hover)').matches &&
      window.matchMedia('(pointer: fine)').matches
    );
  }

  onSummaryClick(event) {
    if (!this.shouldUseHoverInteraction()) return;
    if (event.detail === 0) return;

    event.preventDefault();
    requestAnimationFrame(() => {
      this.summary.setAttribute('aria-expanded', this.mainDetailsToggle.hasAttribute('open'));
    });
  }

  onMouseEnter() {
    if (!this.shouldUseHoverInteraction()) return;

    clearTimeout(this.closeTimer);
    this.closeSiblingMenus();
    this.open();
  }

  onMouseLeave() {
    if (!this.shouldUseHoverInteraction()) return;

    clearTimeout(this.closeTimer);
    this.closeTimer = window.setTimeout(() => {
      this.close();
    }, 120);
  }

  closeSiblingMenus() {
    document.querySelectorAll('header-menu details[open]').forEach((openDetails) => {
      if (openDetails === this.mainDetailsToggle) return;

      openDetails.removeAttribute('open');
      openDetails.querySelector('summary')?.setAttribute('aria-expanded', false);
    });
  }

  isSimpleDropdown() {
    return this.content?.classList.contains('bstaendig-simple-menu__content');
  }

  getSimpleDropdownHandle() {
    const summaryId = this.summary?.id || '';

    if (!summaryId.startsWith('HeaderMenu-')) {
      return '';
    }

    return summaryId.replace('HeaderMenu-', '');
  }

  getSimpleDropdownOffset() {
    const handle = this.getSimpleDropdownHandle();

    if (handle === 'uber-uns') {
      return -18;
    }

    return 0;
  }

  resetSimpleDropdownPosition() {
    if (!this.isSimpleDropdown()) return;

    this.content.style.removeProperty('--bst-simple-dropdown-left');
  }

  positionSimpleDropdown() {
    if (!this.isSimpleDropdown() || !this.shouldUseHoverInteraction()) {
      this.resetSimpleDropdownPosition();
      return;
    }

    const nav = this.closest('.bstaendig-header-nav');
    if (!nav) return;

    const navRect = nav.getBoundingClientRect();
    const summaryRect = this.summary.getBoundingClientRect();
    const contentRect = this.content.getBoundingClientRect();
    const contentWidth = contentRect.width || this.content.scrollWidth;
    const preferredLeft = summaryRect.left - navRect.left - 10 + this.getSimpleDropdownOffset();
    const maxLeft = Math.max(navRect.width - contentWidth, 0);
    const clampedLeft = Math.min(Math.max(preferredLeft, 0), maxLeft);

    this.content.style.setProperty('--bst-simple-dropdown-left', `${Math.round(clampedLeft)}px`);
  }

  open() {
    if (this.mainDetailsToggle.hasAttribute('open')) return;

    this.mainDetailsToggle.setAttribute('open', '');
    this.summary.setAttribute('aria-expanded', true);
    requestAnimationFrame(() => this.positionSimpleDropdown());
  }

  onToggle() {
    super.onToggle();
    if (!this.header) return;
    this.header.preventHide = this.mainDetailsToggle.open;

    if (this.mainDetailsToggle.open) {
      this.positionSimpleDropdown();
    } else {
      this.resetSimpleDropdownPosition();
    }

    if (document.documentElement.style.getPropertyValue('--header-bottom-position-desktop') !== '') return;
    document.documentElement.style.setProperty(
      '--header-bottom-position-desktop',
      `${Math.floor(this.header.getBoundingClientRect().bottom)}px`
    );
  }
}

customElements.define('header-menu', HeaderMenu);
